/**
 * DefensibilityStatementPage.tsx — public-facing statement that proves the
 *                                   platform's outputs are defensible against
 *                                   engineering, regulatory and legal scrutiny.
 */

import { useNavigate } from 'react-router';
import {
  Scale, ArrowLeft, ShieldCheck, AlertTriangle, FileText, Library,
  CheckCircle2, XCircle, Beaker, Settings, GitBranch, Eye,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  REGULATORY_THRESHOLDS, MODEL_ASSUMPTIONS, CALIBRATION_BOUNDS,
  PLATFORM_LIMITATIONS, VERIFICATION_STATUS, REPRODUCIBILITY,
  CONFLICT_DISCLOSURES, DEFENSIBILITY_HEADLINE,
} from '@/lib/defensibility';

export default function DefensibilityStatementPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Scale} title="Defensibility Statement"
        subtitle="Evidence, assumptions, limits and provenance that make every output of this platform defensible"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Headline */}
      <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4">
        <div className="text-sm font-semibold text-emerald-100 flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-emerald-300" />
          Headline
        </div>
        <p className="text-sm text-emerald-50 leading-relaxed">{DEFENSIBILITY_HEADLINE}</p>
      </div>

      {/* 1. Regulatory thresholds */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4 text-fuchsia-300" />
          1. Regulatory thresholds — full citation chain
        </div>
        <p className="text-xs text-slate-400 mb-3">Every numeric threshold used in compliance scoring is traceable to a primary statutory or guidance document. Reference text below is quoted verbatim from the source legislation / standard.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="border-b-2 border-slate-700 text-slate-400">
              <tr>
                <th className="py-2 pr-2">Parameter</th>
                <th className="py-2 pr-2">Threshold</th>
                <th className="py-2 pr-2">Force</th>
                <th className="py-2 pr-2">Regulation</th>
                <th className="py-2 pr-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {REGULATORY_THRESHOLDS.map((r, i) => (
                <tr key={i} className="border-b border-slate-800/80 align-top">
                  <td className="py-1.5 pr-2 text-slate-100 font-semibold">{r.parameter}</td>
                  <td className="py-1.5 pr-2 text-emerald-300 font-mono font-bold">{r.threshold}</td>
                  <td className="py-1.5 pr-2">
                    <ForceTag force={r.legalForce} jurisdiction={r.jurisdiction} />
                  </td>
                  <td className="py-1.5 pr-2 text-slate-300">{r.regulation}</td>
                  <td className="py-1.5 pr-2 text-slate-400 italic max-w-xs">{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 2. Model assumptions */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <Settings className="w-4 h-4 text-sky-300" />
          2. Model assumptions — what each module assumes is true
        </div>
        <p className="text-xs text-slate-400 mb-3">Every module is grounded on assumptions documented in its primary source. Where the assumption breaks, the module fails gracefully with a documented impact.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="border-b-2 border-slate-700 text-slate-400">
              <tr>
                <th className="py-2 pr-2">Module</th>
                <th className="py-2 pr-2">Assumption</th>
                <th className="py-2 pr-2">Valid when</th>
                <th className="py-2 pr-2">If violated</th>
              </tr>
            </thead>
            <tbody>
              {MODEL_ASSUMPTIONS.map((a, i) => (
                <tr key={i} className="border-b border-slate-800/80 align-top">
                  <td className="py-1.5 pr-2 text-slate-100 font-mono font-bold">{a.module}</td>
                  <td className="py-1.5 pr-2 text-slate-300">{a.assumption}</td>
                  <td className="py-1.5 pr-2 text-emerald-300 italic">{a.validityRegime}</td>
                  <td className="py-1.5 pr-2 text-amber-300 italic">{a.impactIfViolated}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 3. Calibration bounds */}
      <section className="rounded-2xl border-2 border-amber-700/60 bg-amber-950/20 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <Beaker className="w-4 h-4 text-amber-300" />
          3. Calibration vs Barcombe — where models are in / out of range
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Honest accounting of where Barcombe sits inside vs outside each module's published calibration range. Modules marked
          <strong className="text-amber-200"> "OUT-OF-RANGE"</strong> are still presented for completeness but their predictions carry larger uncertainty
          and must be qualified accordingly in any external submission.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="border-b-2 border-amber-700/60 text-slate-400">
              <tr>
                <th className="py-2 pr-2">Module</th>
                <th className="py-2 pr-2">Variable</th>
                <th className="py-2 pr-2">Published range</th>
                <th className="py-2 pr-2">Barcombe value</th>
                <th className="py-2 pr-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {CALIBRATION_BOUNDS.map((c, i) => (
                <tr key={i} className="border-b border-amber-900/40 align-top">
                  <td className="py-1.5 pr-2 text-slate-100 font-mono font-bold">{c.module}</td>
                  <td className="py-1.5 pr-2 text-slate-300">{c.variable}</td>
                  <td className="py-1.5 pr-2 text-slate-400 italic">{c.calibratedRange}</td>
                  <td className="py-1.5 pr-2 text-slate-200 font-mono">{c.barcombeValue}</td>
                  <td className="py-1.5 pr-2">
                    {c.inRange ? (
                      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-emerald-900/40 text-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> IN RANGE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-md border border-amber-700 bg-amber-900/40 text-amber-200 px-1.5 py-0.5 text-[10px] font-semibold">
                        <AlertTriangle className="w-3 h-3" /> OUT-OF-RANGE
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 4. Platform limitations */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <XCircle className="w-4 h-4 text-red-300" />
          4. Platform limitations — what the tool does NOT do
        </div>
        <p className="text-xs text-slate-400 mb-3">An honest list of what this platform is unable to deliver and the recommended workaround.</p>
        <div className="space-y-2">
          {PLATFORM_LIMITATIONS.map((l, i) => (
            <div key={i} className="rounded-lg border border-red-700/40 bg-red-900/10 p-3">
              <div className="text-sm font-bold text-red-200">{l.area}</div>
              <div className="text-xs text-slate-300 mt-1">{l.limitation}</div>
              <div className="text-xs text-emerald-300 italic mt-1.5">
                <strong>Workaround:</strong> {l.workaround}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Verification status */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <Eye className="w-4 h-4 text-sky-300" />
          5. Verification status — what has and has not been peer-reviewed
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="border-b-2 border-slate-700 text-slate-400">
              <tr>
                <th className="py-2 pr-2">Scope</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2">Evidence</th>
              </tr>
            </thead>
            <tbody>
              {VERIFICATION_STATUS.map((v, i) => (
                <tr key={i} className="border-b border-slate-800/80 align-top">
                  <td className="py-1.5 pr-2 text-slate-100 font-semibold">{v.scope}</td>
                  <td className="py-1.5 pr-2"><VerifTag status={v.status} /></td>
                  <td className="py-1.5 pr-2 text-slate-400 italic">{v.evidence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 6. Reproducibility */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-fuchsia-300" />
          6. Reproducibility metadata
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <Field k="Platform version" v={REPRODUCIBILITY.version} />
          <Field k="Build date" v={REPRODUCIBILITY.buildDate} />
          <Field k="RNG strategy" v={REPRODUCIBILITY.rngStrategy} fullWidth />
        </div>
        <div className="mt-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Dependency versions</div>
          <div className="flex flex-wrap gap-1.5">
            {REPRODUCIBILITY.deps.map((d, i) => (
              <span key={i} className="inline-block rounded-md border border-slate-700 bg-slate-800 px-2 py-0.5 text-[10px] font-mono text-slate-300">
                {d.name} {d.version}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Conflict disclosures */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-2 flex items-center gap-2">
          <Library className="w-4 h-4 text-slate-300" />
          7. Conflict-of-interest disclosures
        </div>
        <div className="space-y-2 text-xs">
          {CONFLICT_DISCLOSURES.map((c, i) => (
            <div key={i} className="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
              <div className="text-sm font-bold text-slate-100">{c.party}</div>
              <div className="text-slate-400 mt-1 leading-snug">{c.disclosure}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer note */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">For use in regulatory submissions:</strong> attach this statement (and the
        confidential PDF report from any Train Summary page) as an appendix to AMP-8 / DWI / EA submissions. The combined
        document chain provides full traceability from a single numerical claim → module → equation → primary source
        → regulatory threshold. A chartered CWEM / CEnv / SQEP review is recommended before relying on the framework for any
        legally-binding determination; see Section 5 above.
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────

function ForceTag({ force, jurisdiction }: { force: string; jurisdiction: string }) {
  const cls = force === 'statutory PCV'
    ? 'border-red-700 bg-red-900/40 text-red-200'
    : force === 'guideline'
      ? 'border-amber-700 bg-amber-900/40 text-amber-200'
      : force === 'standard'
        ? 'border-sky-700 bg-sky-900/40 text-sky-200'
        : 'border-slate-700 bg-slate-900 text-slate-300';
  return (
    <div className="space-y-0.5">
      <div className={`inline-block rounded-md border px-1.5 py-0.5 text-[9px] font-semibold ${cls}`}>{force.toUpperCase()}</div>
      <div className="text-[9px] text-slate-500">{jurisdiction}</div>
    </div>
  );
}

function VerifTag({ status }: { status: string }) {
  const cls = status === 'peer-reviewed source'
    ? 'border-emerald-700 bg-emerald-900/40 text-emerald-200'
    : status === 'self-verified'
      ? 'border-amber-700 bg-amber-900/40 text-amber-200'
      : status === 'independent test'
        ? 'border-sky-700 bg-sky-900/40 text-sky-200'
        : 'border-slate-700 bg-slate-900 text-slate-400';
  return <span className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{status}</span>;
}

function Field({ k, v, fullWidth = false }: { k: string; v: string; fullWidth?: boolean }) {
  return (
    <div className={`rounded-lg border border-slate-700 bg-slate-950/40 p-3 ${fullWidth ? 'md:col-span-2' : ''}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-slate-200 mt-0.5">{v}</div>
    </div>
  );
}
